import React, { useState } from 'react'
import dayjs from 'dayjs'
import { ReactComponent as CheckMarkIcon } from '../../assets/img/icons/check-mark.svg'
import { ModalWindowCallbackHandler } from '../../store/ModalWindowData/types'
import { NotificationFrequency, NotificationScheduleDto, ScheduledNotificationDto, Weekday } from '../../sdk'
import { SWEDISH_TIME_ZONE, WEEKDAY_LABELS } from '../../utils/scheduledNotifications'
import { FormField, ModalHeader } from './EventModals'

export type ManageScheduledNotificationModalData = {
  notification: ScheduledNotificationDto | null
}

type ManageScheduledNotificationModalProps = {
  onConfirm: ModalWindowCallbackHandler<'createScheduledNotification' | 'updateScheduledNotification' | 'cancel'>
  data: ManageScheduledNotificationModalData
}

type DeleteScheduledNotificationModalProps = {
  onConfirm: ModalWindowCallbackHandler<'deleteScheduledNotification' | 'cancel'>
  data: { id: string; title: string }
}

type SendMode = 'now' | 'once' | 'weekly'

type WeekdayCheckboxProps = {
  day: Weekday
  isChecked: boolean
  onToggle: (day: Weekday) => void
}

// Value format of datetime-local inputs, without a time zone
const DATETIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm'
const TITLE_MAX_LENGTH = 100
const BODY_MAX_LENGTH = 500
const DEFAULT_TIME = '09:00'

const SEND_MODE_LABELS: Record<SendMode, string> = {
  now: 'Send now',
  once: 'One time',
  weekly: 'Every week',
}

const getInitialSendMode = (notification: ScheduledNotificationDto | null): SendMode => {
  if (!notification) return 'weekly'
  return notification.schedule.frequency === NotificationFrequency.WEEKLY ? 'weekly' : 'once'
}

const toSwedishInputValue = (iso?: string): string =>
  iso ? dayjs(iso).tz(SWEDISH_TIME_ZONE).format(DATETIME_LOCAL_FORMAT) : ''

function getSendAtError(sendAt: string): string | undefined {
  if (!sendAt) return 'Date and time are required'
  if (dayjs.tz(sendAt, SWEDISH_TIME_ZONE).isBefore(dayjs(), 'minute')) {
    return 'Pick a time in the future, or choose Send now'
  }
  return undefined
}

function buildSchedule(mode: SendMode, sendAt: string, weekdays: Weekday[], time: string): NotificationScheduleDto {
  switch (mode) {
    case 'now':
      return { frequency: NotificationFrequency.ONCE, sendAt: new Date().toISOString() }
    case 'once':
      return { frequency: NotificationFrequency.ONCE, sendAt: dayjs.tz(sendAt, SWEDISH_TIME_ZONE).toISOString() }
    case 'weekly':
      return {
        frequency: NotificationFrequency.WEEKLY,
        // Calendar order, whatever order the days were ticked in
        weekdays: Object.values(Weekday).filter(day => weekdays.includes(day)),
        time,
        timezone: SWEDISH_TIME_ZONE,
      }
  }
}

const WeekdayCheckbox = ({ day, isChecked, onToggle }: WeekdayCheckboxProps): React.JSX.Element => (
  <label style={{ display: 'flex', alignItems: 'center', margin: 0, fontSize: '14px', cursor: 'pointer' }}>
    <div className="custom-checkbox_wrapper margin-right">
      <input type="checkbox" className="hidden-checkbox" checked={isChecked} onChange={() => onToggle(day)} />
      <div className="pseudo-checkbox_holder"></div>
      <CheckMarkIcon className="pseudo-checkbox_icon" />
    </div>
    {WEEKDAY_LABELS[day]}
  </label>
)

export const ManageScheduledNotificationModal = ({
  data,
  onConfirm,
}: ManageScheduledNotificationModalProps): React.JSX.Element => {
  const { notification } = data

  const [title, setTitle] = useState(notification?.title || '')
  const [body, setBody] = useState(notification?.body || '')
  const [sendMode, setSendMode] = useState<SendMode>(getInitialSendMode(notification))
  const [sendAt, setSendAt] = useState(toSwedishInputValue(notification?.schedule.sendAt))
  const [weekdays, setWeekdays] = useState<Weekday[]>(notification?.schedule.weekdays || [])
  const [time, setTime] = useState(notification?.schedule.time || DEFAULT_TIME)

  const titleError = title.trim() ? undefined : 'Title is required'
  const bodyError = body.trim() ? undefined : 'Description is required'
  const sendAtError = sendMode === 'once' ? getSendAtError(sendAt) : undefined
  const weekdaysError = sendMode === 'weekly' && weekdays.length === 0 ? 'Pick at least one day' : undefined
  const timeError = sendMode === 'weekly' && !time ? 'Time is required' : undefined
  const isValid = !titleError && !bodyError && !sendAtError && !weekdaysError && !timeError

  const toggleWeekday = (day: Weekday) =>
    setWeekdays(current => (current.includes(day) ? current.filter(d => d !== day) : [...current, day]))

  const handleCancel = () => onConfirm({ action: 'cancel', data: null })

  const handleSubmit = () => {
    if (!isValid) return

    const dto = {
      title: title.trim(),
      body: body.trim(),
      schedule: buildSchedule(sendMode, sendAt, weekdays, time),
    }

    if (notification) {
      onConfirm({ action: 'updateScheduledNotification', data: { id: notification.id, ...dto } })
    } else {
      onConfirm({ action: 'createScheduledNotification', data: dto })
    }
  }

  return (
    <div className="event-modal">
      <ModalHeader title={notification ? 'Edit notification' : 'Create notification'} onClose={handleCancel} />

      <div className="event-modal_body">
        {/* Errors sit absolutely below their field, so the last field's error needs room to show */}
        <div className="input-holder flex-item item-sm-6" style={{ paddingBottom: '16px' }}>
          <p style={{ margin: '12px 0 0', color: '#888' }}>
            Sent as a push notification to all patients who have notifications turned on.
          </p>

          <FormField label="Title *" error={titleError}>
            <input
              type="text"
              value={title}
              maxLength={TITLE_MAX_LENGTH}
              onChange={e => setTitle(e.target.value)}
              placeholder="Notification title"
            />
          </FormField>

          <FormField label="Description *" error={bodyError}>
            <textarea
              value={body}
              maxLength={BODY_MAX_LENGTH}
              onChange={e => setBody(e.target.value)}
              placeholder="Notification text"
              rows={3}
              style={{ width: '100%', padding: '8px', resize: 'vertical' }}
            />
          </FormField>

          <FormField label="When">
            <select value={sendMode} onChange={e => setSendMode(e.target.value as SendMode)}>
              {(Object.keys(SEND_MODE_LABELS) as SendMode[]).map(mode => (
                <option key={mode} value={mode}>
                  {SEND_MODE_LABELS[mode]}
                </option>
              ))}
            </select>
          </FormField>

          {sendMode === 'once' && (
            <FormField label="Date & time (Swedish time) *" error={sendAtError}>
              <input type="datetime-local" value={sendAt} onChange={e => setSendAt(e.target.value)} />
            </FormField>
          )}

          {sendMode === 'weekly' && (
            <>
              <FormField label="Days *" error={weekdaysError}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', padding: '4px 0' }}>
                  {Object.values(Weekday).map(day => (
                    <WeekdayCheckbox
                      key={day}
                      day={day}
                      isChecked={weekdays.includes(day)}
                      onToggle={toggleWeekday}
                    />
                  ))}
                </div>
              </FormField>

              <FormField label="Time (Swedish time) *" error={timeError}>
                <input type="time" value={time} onChange={e => setTime(e.target.value)} />
              </FormField>
            </>
          )}
        </div>
      </div>

      <div className="note-popup_footer" style={{ marginTop: '20px' }}>
        <button className="g_btn g_btn--primary" onClick={handleSubmit} disabled={!isValid}>
          {sendMode === 'now' ? 'Send' : 'Save'}
        </button>
        <button className="g_btn g_btn--outlined" onClick={handleCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

export const DeleteScheduledNotificationModal = ({
  data,
  onConfirm,
}: DeleteScheduledNotificationModalProps): React.JSX.Element => {
  const handleCancel = () => onConfirm({ action: 'cancel', data: null })

  return (
    <div>
      <ModalHeader title="Delete notification" onClose={handleCancel} />

      <div style={{ padding: '16px 0' }}>
        Are you sure you want to delete <strong>&ldquo;{data.title}&rdquo;</strong>? Any pushes not sent yet will be
        cancelled.
      </div>

      <div className="note-popup_footer">
        <button
          className="g_btn g_btn--primary"
          style={{ background: '#BA1717', borderColor: '#BA1717' }}
          onClick={() => onConfirm({ action: 'deleteScheduledNotification', data: data.id })}
        >
          Delete
        </button>
        <button className="g_btn g_btn--outlined" onClick={handleCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}
